import { goto } from '$app/navigation';
import { page } from '$app/stores';
import { signIn, signOut as authSignOut } from '@auth/sveltekit/client';
import { FEATURE_FLAGS } from '@repo/sharedlib/src/utils/constants';
import { get } from 'svelte/store';

/**
 * Merges additional query parameters with an existing URL's query string
 * @param url - The base URL that may contain existing query parameters
 * @param additionalParams - Additional parameters to merge
 * @returns The URL with merged query parameters
 *
 * @example
 * mergeQueryStrings("myGreatUrl?foo=bar", { "foo2": "bar2" })
 * // Returns: "myGreatUrl?foo=bar&foo2=bar2"
 */
export const mergeQueryStrings = (
	url: string,
	additionalParams: Record<string, string> = {}
): string => {
	if (!additionalParams || Object.keys(additionalParams).length === 0) {
		return url;
	}

	try {
		const urlObj = new URL(url, window.location.origin);
		const existingParams = new URLSearchParams(urlObj.search);
		const newParams = new URLSearchParams(additionalParams);

		// Merge the parameters
		for (const [key, value] of newParams.entries()) {
			existingParams.set(key, value);
		}

		// Reconstruct the URL
		urlObj.search = existingParams.toString();
		return urlObj.toString();
	} catch {
		// Fallback for relative URLs or invalid URLs
		const separator = url.includes('?') ? '&' : '?';
		const queryString = new URLSearchParams(additionalParams).toString();
		return queryString ? `${url}${separator}${queryString}` : url;
	}
};

/* Adds a callbackUrl for Retain to use to redirect to after sign in */
export const DEFAULT_POST_SIGN_IN_ROUTE = FEATURE_FLAGS['web-app'] ? '/app/start' : '/protected';

export const signInWithCallbackUrl = async (
	fallbackUrl: string = DEFAULT_POST_SIGN_IN_ROUTE,
	additionalParams: Record<string, string> = {}
) => {
	const $page = get(page);
	const myCallbackUrl = $page.url.searchParams.get('callbackUrl') || fallbackUrl;
	const mergedUrl = mergeQueryStrings(myCallbackUrl, additionalParams);
	console.log('signing in with mergedUrl', mergedUrl);

	const result = await signIn('google', { callbackUrl: mergedUrl });
	return result;
};

export const signUpWithCallbackUrl = async (fallbackUrl: string = DEFAULT_POST_SIGN_IN_ROUTE) => {
	const $page = get(page);
	const myCallbackUrl = $page.url.searchParams.get('callbackUrl') || fallbackUrl;
	const encodedCallbackUrl = encodeURIComponent(myCallbackUrl);
	goto(`/sign-up?callbackUrl=${encodedCallbackUrl}`);
};

export const signOut = async () => {
	// Clear user ID in Google Analytics before signing out
	if (typeof window.gtag !== 'undefined') {
		window.gtag('config', 'G-P086B5QL6N', {
			user_id: null
		});
	}

	// Perform the actual sign out
	return authSignOut();
};
